import constate from 'constate';
import { useMemo } from 'react';
import * as uuid from 'uuid';

import { api, isDefined, useLoggedInAuthContext } from 'common';

import { DEFAULT_EDGE_PROPS, DEFAULT_EDGE_TYPE } from './ReactFlow/Edges/constants';
import {
  DEFAULT_START_NODE_POSITION,
  createStartNode,
} from './ReactFlow/Nodes/Configs/Start/utils';
import { UiPlaceholderBlockType } from './ReactFlow/types';
import { EdgeHandleType, type ReactFlowNode, isStartBlockNode } from './types';
import { convertFlowToReactFlow, createPlaceholderNode } from './utils';

interface FlowDefinitionProps {
  shouldInitializeWithStartNodes?: boolean;
  flowDefinition: api.FlowDefinition.FlowDefinitionWithFlow;
}

function useFlowDefinition(props: FlowDefinitionProps) {
  const { shouldInitializeWithStartNodes, flowDefinition } = props;
  const { checkAgentPermissions } = useLoggedInAuthContext();

  const isFlowDefinitionEditable =
    api.FlowDefinition.FlowDefinitionStateProps[flowDefinition.state].isEditable;

  const reactFlowNodesAndEdges = useMemo(() => {
    const { nodes, edges } = convertFlowToReactFlow(flowDefinition.data);
    if (shouldInitializeWithStartNodes && isFlowDefinitionEditable) {
      let startBlock: ReactFlowNode | undefined = nodes.find((node) => isStartBlockNode(node));
      if (!isDefined(startBlock)) {
        startBlock = createStartNode({
          position: DEFAULT_START_NODE_POSITION,
        });
        nodes.push(startBlock);
      }
      if (nodes.length === 1 && isStartBlockNode(nodes[0])) {
        const placeholderNode = createPlaceholderNode({
          position: { x: 24 * 16, y: 8 * 16 },
          uiBlockType: UiPlaceholderBlockType.BasePlaceholder,
        });
        nodes.push(placeholderNode);
        edges.push({
          id: uuid.v4(),
          source: startBlock.id,
          target: placeholderNode.id,
          edgeHandleType: EdgeHandleType.Default,
          type: DEFAULT_EDGE_TYPE,
          ...DEFAULT_EDGE_PROPS,
        });
      }
    }
    return { nodes, edges };
  }, [flowDefinition, shouldInitializeWithStartNodes, isFlowDefinitionEditable]);

  const flowDefinitionPermissions = checkAgentPermissions({
    agentId: flowDefinition.agentId ?? undefined,
    organizationId: flowDefinition.organizationId,
  });

  return {
    flowDefinition,
    isFlowDefinitionEditable,
    reactFlowNodesAndEdges,
    flowDefinitionPermissions,
  };
}

export const [FlowDefinitionProvider, useFlowDefinitionContext] = constate(useFlowDefinition);
